import $http from './http'
import qs from 'qs'

/* =========================================================================
 * 人事档案模块 —— 统一 API 层（mock / 真实接口 混合）
 * -------------------------------------------------------------------------
 * USE_MOCK 仍为 true（全局开关），仅用于「页面3-4 列表/详情/增删改/三类审批」尚未逐页对接的部分。
 * 已接真实后端（接口说明.txt 2026-10-28 + 接口说明2.txt 2026-10-30）：
 *   ✅ 页面1：saveApprovalSetting / deleteApprovalSetting / getApprovalSettings
 *   ✅ 页面2 转正提醒：createRegularizationReminder / updateRegularizationReminder
 *              / deleteRegularizationReminder / getRegularizationReminders（无审批，流转靠编辑保存+提醒）
 *   ✅ 页面3-4 列表：getArchiveApplyList / getArchiveAuditList（接口说明2 #5/#11，已接真实）
 *   ✅ 页面3-4 审批(转正-部门负责人阶段 process_status==0)：auditRegularArchives（接口说明2 #7 简化版，已接真实）
 *   ✅ 页面3-4 详情/保存/提交/三类审批：getArchiveDetail(#6) / saveArchive→editArchives(#3) / submitArchive→createArchives(#2)
 *              / auditArchive(按type路由 #7/#8/#10) —— 均已接真实接口，不受 USE_MOCK 控制。
 *   ✅ 删除：deleteArchiveApplication（#4，已接真实；转正申请 type0 后端不支持删除）。
 * USE_MOCK 仅在这些未接函数内生效；已接真实的函数不再受其控制。
 *
 * 统一返回格式（与现有列表接口一致，取自 approval_settings.vue）：
 *   res.data.status === 1 成功
 *   res.data.data.data | .list | .data   列表数组
 *   res.data.data.last_page | .pagination.total_pages   总页数
 *   res.data.data.total | .pagination.total             总条数
 *   res.data.message   错误信息
 * ========================================================================= */

export const USE_MOCK = true

/* ---------- 跨标签页刷新（审批/编辑在别的标签页操作后通知本页刷新）----------
 * 项目里 vuex 是单标签实例、不能跨标签；App.vue 的 storage 监听只处理 userInfo。
 * 故自管一个 localStorage key，列表页监听该 key 的 storage 事件即可自动刷新。 */
export const ARCHIVE_REFRESH_KEY = 'archiveRefresh'
export function notifyArchiveRefresh () {
  try { localStorage.setItem(ARCHIVE_REFRESH_KEY, String(Date.now())) } catch (e) {}
}
export function onArchiveRefresh (cb) {
  const handler = (e) => { if (e.key === ARCHIVE_REFRESH_KEY) cb && cb(e) }
  window.addEventListener('storage', handler)
  return () => window.removeEventListener('storage', handler)
}

/* =========================================================================
 * 页面1：档案审批设置（人事审批配置 / 总经办审批配置）
 * 后端接口（接口说明.txt 2026-10-28）：
 *   设置/删除：POST /v1/attendance/setPersonnelArchivesApprover
 *             { type:0人事/1总经办/2部门小组, emp_id:Int, depart_ids:Array[478,521] }
 *             （删除 = 传空 depart_ids 覆盖保存）
 *   列表：    GET  /v1/attendance/getPersonnelArchivesApprover  { type, emp_name }
 *             —— 已接真实请求（2026-10-28 你 F12 实测）。无翻页，返回一维数组：
 *                res.data.data = [{ emp_id, emp_name, depart_data:[{depart_id,depart_name}] }]
 *             type 映射：hr => 0(人事审批)  gm => 1(总经办审批)  dept => 2(部门小组审批)
 *   说明：设置/删除/列表 均已接入真实接口；页面2 新建/编辑/删除已接真实（后端逻辑待完善），
 *        页面2 列表 / 页面3-4 列表 / 详情四类接口 说明尚未提供，仍走 mock。
 * ========================================================================= */
const APPROVAL_SETTING_API = {
  set: '/v1/attendance/setPersonnelArchivesApprover',
  list: '/v1/attendance/getPersonnelArchivesApprover'
}
const _typeToInt = (type) => (type === 'gm' ? 1 : type === 'dept' ? 2 : 0)

export function getApprovalSettings (type, params = {}) {
  // 已接入真实接口（接口说明.txt 2026-10-28）。无翻页：返回一维数组 res.data.data = [{ emp_id, emp_name, depart_data:[{depart_id,depart_name}] }]
  const query = qs.stringify({
    type: _typeToInt(type),
    emp_name: params.emp_name || ''
  })
  return $http.get(APPROVAL_SETTING_API.list + '?' + query)
}

export function saveApprovalSetting (type, payload) {
  // 已接入真实接口（接口说明.txt 2026-10-28）。payload: { emp_id, emp_name, depart_ids: number[] }
  return $http.post(APPROVAL_SETTING_API.set, {
    type: _typeToInt(type),
    emp_id: payload.emp_id,
    depart_ids: JSON.stringify(payload.depart_ids || [])
  })
}

export function deleteApprovalSetting (type, emp_id) {
  // 设置接口无独立删除，删除 = 传空 depart_ids 覆盖保存（已接入真实接口）
  return $http.post(APPROVAL_SETTING_API.set, {
    type: _typeToInt(type),
    emp_id: emp_id,
    depart_ids: '[]'
  })
}

/* =========================================================================
 * 页面2：转正提醒（HR 发起；无审批——流转靠"编辑保存自动转下一位编辑人 + 提醒弹框"）
 * 后端接口：接口说明.txt(2026-10-28) 新建/编辑/删除 + 接口说明2.txt(2026-10-30) 列表
 *   新建：POST /v1/attendance/createRegularRemind
 *   编辑：POST /v1/attendance/editRegularRemind
 *   删除：DELETE /v1/attendance/delRegularRemind  （$http.del，body:{id}）
 *   参数：emp_id(Int), join_date, regular_date, probation_period(Int),
 *         original_contract_start/end, current_contract_start/end,
 *         probation_period_salary(Float), regular_salary(Float)
 *   列表：GET  /v1/attendance/getRegularRemindList  参数见 getRegularizationReminders（Laravel 分页）
 *         响应 data = { current_page, data:[...], total, last_page, per_page }，取 data.data 为数组。
 *   注：转正提醒无审批接口；"提醒"相关功能点用户尚未给出，待所有接口接好后再补。
 * ========================================================================= */
const RR_API = {
  create: '/v1/attendance/createRegularRemind',
  edit: '/v1/attendance/editRegularRemind',
  del: '/v1/attendance/delRegularRemind',
  list: '/v1/attendance/getRegularRemindList'
}
// 组件 payload(字段沿用页面旧约定) -> 后端字段 映射
function _rrContract (v) {
  if (!v) return []
  if (Array.isArray(v)) return v
  try { const a = JSON.parse(v); return Array.isArray(a) ? a : [] } catch (e) { return [] }
}
function _rrPayload (payload) {
  const c = _rrContract(payload.current_contract)
  const o = _rrContract(payload.original_contract)
  return {
    emp_id: payload.emp_id,
    join_date: payload.entry_date,
    regular_date: payload.regular_date,
    probation_period: payload.probation_month,
    original_contract_start: o[0] || '',
    original_contract_end: o[1] || '',
    current_contract_start: c[0] || '',
    current_contract_end: c[1] || '',
    probation_period_salary: payload.probation_salary,
    regular_salary: payload.regular_salary
  }
}

export function getRegularizationReminders (param = {}) {
  // 已接真实接口（接口说明2.txt #1）：GET /v1/attendance/getRegularRemindList，后端 Laravel 分页。
  // 组件传入 keyword（员工/流水号搜索）+ page/pageSize；此处映射到后端字段 emp_name。
  const query = qs.stringify({
    page: param.page || 1,
    pageSize: param.pageSize || 10,
    serial_number: param.serial_number || '',
    emp_name: param.emp_name || param.keyword || '',
    created_at: param.created_at || '',
    audit_emp_name: param.audit_emp_name || '',
    status: param.status !== undefined && param.status !== '' ? param.status : '',
    process_status: param.process_status !== undefined && param.process_status !== '' ? param.process_status : ''
  })
  return $http.get(RR_API.list + '?' + query)
}

export function createRegularizationReminder (payload) {
  // 已接入真实接口（接口说明.txt 2026-10-28）
  return $http.post(RR_API.create, _rrPayload(payload))
}

export function updateRegularizationReminder (id, payload) {
  // 已接入真实接口（接口说明.txt 2026-10-28）
  return $http.post(RR_API.edit, Object.assign({ id: id }, _rrPayload(payload)))
}

export function deleteRegularizationReminder (id) {
  // 已接入真实接口（接口说明.txt 2026-10-28）：DELETE + body {id}
  return $http.del(RR_API.del, { id: id })
}

/* =========================================================================
 * 页面3/4：档案申请 / 档案审批 列表（type: 'regular' 转正 / 'salary' 调薪 / 'leave' 离职）
 * 申请页(/archive_application) 与 审批页(/archive_approval) 共用同一组件，
 * 但后端两套独立列表接口（接口说明2.txt #5 / #11）：
 *   - 申请列表：GET /v1/attendance/getArchivesApplyList
 *       params: type, serial_number, add_emp_name, emp_name, created_at,
 *               audit_emp_name, status, process_status, page, pageSize
 *   - 审批列表：GET /v1/attendance/getArchivesAuditList
 *       params: 同上 + is_self(0-全部,1-自己)
 * 两者均 Laravel 分页：res.data.data = { data:[...], total, last_page, per_page }
 * 已接入真实接口（2026-10-30）。列表字段以你 F12 实测为准。
 * ========================================================================= */
const ARCHIVE_APPLY_LIST_API = '/v1/attendance/getArchivesApplyList'
const ARCHIVE_AUDIT_LIST_API = '/v1/attendance/getArchivesAuditList'
const _archiveTypeToInt = (type) => (type === 'salary' ? 1 : type === 'leave' ? 2 : 0)

// 通用列表查询参数拼装（申请/审批共用）
function _archiveListQuery (type, param) {
  return qs.stringify({
    type: _archiveTypeToInt(type),
    page: param.page || 1,
    pageSize: param.pageSize || 10,
    serial_number: param.serial_number || '',
    add_emp_name: param.add_emp_name || '',
    emp_name: param.emp_name || '',
    created_at: param.created_at || '',
    audit_emp_name: param.audit_emp_name || '',
    status: param.status !== undefined && param.status !== '' ? param.status : '',
    process_status: param.process_status !== undefined && param.process_status !== '' ? param.process_status : ''
  })
}

export function getArchiveApplyList (type, param = {}) {
  // 已接真实接口（接口说明2.txt #5）
  return $http.get(ARCHIVE_APPLY_LIST_API + '?' + _archiveListQuery(type, param))
}

export function getArchiveAuditList (type, param = {}) {
  // 已接真实接口（接口说明2.txt #11）；审批页额外带 is_self（0-全部 1-自己）
  const q = _archiveListQuery(type, param)
  const extra = qs.stringify({ is_self: param.is_self !== undefined && param.is_self !== '' ? param.is_self : 0 })
  return $http.get(ARCHIVE_AUDIT_LIST_API + '?' + q + '&' + extra)
}

/* 删除档案申请（接口说明2.txt #4）：DELETE /v1/attendance/delArchives
 * type 仅支持 1-调薪 2-离职；转正申请(type0)来源于转正提醒，后端不支持删除。 */
export function deleteArchiveApplication (type, id) {
  if (type === 'regular') {
    return Promise.reject({ data: { status: 0, message: '转正申请不可删除（来源于转正提醒）' } })
  }
  return $http.del('/v1/attendance/delArchives', { type: _archiveTypeToInt(type), id: id })
}

/* 审批转正申请（接口说明2.txt #7）：POST /v1/attendance/auditRegularArchives
 * 当前阶段（列表 process_status==0 部门负责人审批）仅需 id / audit_result_0 / audit_remark_0：
 *   audit_result_0：1-正常转正 2-延期转正 3-不予转正；>1 时 audit_remark_0 必填驳回原因。
 * 后续阶段（process_status 1~5）走独立详情页 archive_details.vue，不在此弹框内处理。
 * 已接真实接口（2026-10-30），不受 USE_MOCK 控制。 */
export function auditRegularArchives (id, payload) {
  return $http.post('/v1/attendance/auditRegularArchives', Object.assign({ id: id }, payload))
}

/* =========================================================================
 * 申请详情页接口（archive_details.vue 使用）  接口说明2.txt #2/#3/#6/#7/#8/#10
 *   detail: GET  /v1/attendance/getArchivesDetail      { type:Int(0转正/1调薪/2离职), id:Int }
 *   save  : POST /v1/attendance/editArchives           { type, id, ...字段 }  （转正编辑=员工填写/人事等；调薪/离职编辑）
 *   create: POST /v1/attendance/createArchives         { type:1调薪/2离职, ...字段 }  （转正无新增，由提醒生成）
 *   audit : POST /v1/attendance/auditRegularArchives | auditAdjustArchives | auditDimissionArchives
 *           —— 按 type 路由；参数见接口说明2。
 * 已接真实接口，不受 USE_MOCK 控制。type 用 _archiveTypeToInt 转 Int。
 * ========================================================================= */
const DETAIL_API = '/v1/attendance/getArchivesDetail'
const EDIT_API = '/v1/attendance/editArchives'
const CREATE_API = '/v1/attendance/createArchives'
const AUDIT_API = {
  regular: '/v1/attendance/auditRegularArchives',
  salary: '/v1/attendance/auditAdjustArchives',
  leave: '/v1/attendance/auditDimissionArchives'
}

export function getArchiveDetail (type, id) {
  // 接口说明2.txt #6：GET /v1/attendance/getArchivesDetail?type=<Int>&id=<Int>
  // 后端返回 res.data.data = 单条记录（扁平结构，字段同列表项）
  const query = qs.stringify({ type: _archiveTypeToInt(type), id: id || '' })
  return $http.get(DETAIL_API + '?' + query)
}

export function saveArchive (type, id, payload) {
  // 接口说明2.txt #3：POST /v1/attendance/editArchives { type, id, ...字段 }
  return $http.post(EDIT_API, Object.assign({ type: _archiveTypeToInt(type), id: id }, payload))
}

export function submitArchive (type, id, payload) {
  // 接口说明2.txt #2：POST /v1/attendance/createArchives { type, ...字段 }（调薪/离职新增；转正无）
  // 新增无 id，故不传 id
  return $http.post(CREATE_API, Object.assign({ type: _archiveTypeToInt(type) }, payload))
}

export function auditArchive (type, id, stage, payload) {
  // 接口说明2.txt #7/#8/#10：按 type 路由到对应审批端点；payload 由子组件按当前阶段组装
  const api = AUDIT_API[type] || AUDIT_API.regular
  return $http.post(api, Object.assign({ id: id }, payload))
}
